import React from 'react';
import cn from 'classnames';
import { useUrlDataContext } from '../lib/UrlDataContext';
import { getShopProductUrl } from '../../../helpers/helpers';
import { getUrlDataLink, getUrlDataWwwLink } from '../../../helpers/url_data_helpers';
import { IoArrowForwardSharp, IoArrowBackSharp, IoLink, IoEyeOffOutline } from 'react-icons/io5';
import { Image } from './Image';
import { DismissUrlData } from './DismissUrlData';
import './CurrentUrlData.scss';
import { CurrentUrlReservedWarning } from './CurrentUrlReservedWarning';
import { EscalationDetails } from './EscalationDetails';
import { UrlDataOverrides } from './UrlDataOverrides';

export const CurrentUrlData = () => {
  const {
    currentUrlData,
    goToNextUrl,
    goToPreviousUrl,
    hasNextUrl,
    hasPreviousUrl,
    reserving,
    verifyingReservation,
    reservationFailReason,
    isDirectVisitToCurrentUrl,
    allPendingElsewhere,
    excludeDomain
  } = useUrlDataContext();

  if (!currentUrlData) {
    if (allPendingElsewhere) {
      return (
        <div className='current-url-data'>
          <div className='product card all-pending-elsewhere'>
            All remaining URLs here are being worked on by other matchers right now. Checking again shortly...
          </div>
        </div>
      );
    }
    return null;
  }

  const { bestCategoryMatch } = currentUrlData;
  const url = getUrlDataLink(currentUrlData);
  const wwwUrl = getUrlDataWwwLink(currentUrlData);

  const isLoading = reserving || verifyingReservation;
  const loadingMessage = verifyingReservation ? 'Verifying reservation...' : 'Reserving URL...';

  // Only reveal an existing match once the reserve confirmed the row is genuinely blocked as
  // matched, or on a deliberate direct visit — dual-label rows must read as unmatched.
  const showMatchedState = Boolean(currentUrlData.Product_id) && (reservationFailReason === 'matched' || isDirectVisitToCurrentUrl);

  return (
    <div className='current-url-data'>
      {isLoading && (
        <div className='reservation-loading-overlay'>
          <span>{loadingMessage}</span>
        </div>
      )}

      <div className='header'>
        <button className={cn({ hidden: !hasPreviousUrl })} onClick={goToPreviousUrl}>
          <IoArrowBackSharp size={20} />
        </button>

        <div className='domain'>
          <span>{currentUrlData.domain}</span>
          {!isDirectVisitToCurrentUrl && (
            <button className='hide-domain' title={`Hide ${currentUrlData.domain} from the queue`} onClick={() => excludeDomain(currentUrlData.domain)}>
              <IoEyeOffOutline size={16} />
            </button>
          )}
        </div>

        <button className={cn({ hidden: !hasNextUrl })} onClick={goToNextUrl}>
          <IoArrowForwardSharp size={20} />
        </button>
      </div>

      <div className='product card'>
        <CurrentUrlReservedWarning />
        {showMatchedState && (
          <div className='matched-status'>
            Matched to <a href={getShopProductUrl(currentUrlData.Product_id)}>Product {currentUrlData.Product_id}</a>
          </div>
        )}

        <div className='url-links'>
          <a className='url-link' href={url} title={url} target='_blank' rel='noopener noreferrer'>
            <IoLink className='link-icon' size={16} />
            <span>{currentUrlData.urlStem}</span>
          </a>
          {wwwUrl && (
            <a className='www-link' href={wwwUrl} title={`Some sites only load on the www. host: ${wwwUrl}`} target='_blank' rel='noopener noreferrer'>
              Open with www.
            </a>
          )}
        </div>

        {currentUrlData.sourcePageBlocked && !showMatchedState && (
          <p className='source-blocked'>Bot protection blocked our crawler on this page. Open it in your browser to identify the product.</p>
        )}

        <Image imagePath='primaryImageUrl' alt={currentUrlData.productName || ''} />

        <div className='product-information'>
          <h3 className='brand-name'>{currentUrlData.brandName}</h3>
          <h2 className='product-name'>{currentUrlData.productName}</h2>
          <p className='category-name'>{bestCategoryMatch?.confidence?.score > 50 ? bestCategoryMatch.name : currentUrlData.proposedCategoryName}</p>
        </div>

        {currentUrlData.escalationData && (
          <EscalationDetails escalationData={currentUrlData.escalationData} escalatedAt={currentUrlData.escalatedAt} />
        )}

        {currentUrlData.productDescription && <p className='product-description'>{currentUrlData.productDescription}</p>}

        {showMatchedState && <UrlDataOverrides />}
        <DismissUrlData canUnmatch={showMatchedState} />
      </div>
    </div>
  );
};
